import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Medal, Users, RotateCcw } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import BottomTabBar from '../components/BottomTabBar';
import { useAuth } from '../providers/AuthProvider';
import { apiMethods } from '../services/api';
import { getInitials, formatDisplayName, formatDateTimeTH, debugTimezone } from '../utils/textUtils';

const MyMatchesPage = () => {
  const { status, uid, error: authError, retry } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  
  const LIMIT = 20;

  const loadMatches = useCallback(async (reset = false) => {
    if (status !== 'ready' || !uid) return;

    try {
      const currentOffset = reset ? 0 : offset;
      
      if (reset) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const response = await apiMethods.getPlayerMatches(uid, {
        limit: LIMIT,
        offset: currentOffset,
      });

      const newMatches = response.data.items || response.data || [];
      const total = response.data.total || 0;

      // Debug log เพื่อดู opponent avatar และ timezone
      console.log('Matches data:', newMatches);
      if (newMatches.length > 0) {
        console.log('First match opponent_avatar:', newMatches[0].opponent_avatar);
        console.log('First match played_at:', newMatches[0].played_at);
        console.log('Timezone debug:', debugTimezone(newMatches[0].played_at));
        console.log('First match data:', newMatches[0]);
      }

      if (reset) {
        setMatches(newMatches);
        setOffset(LIMIT);
      } else {
        setMatches(prev => [...prev, ...newMatches]);
        setOffset(prev => prev + LIMIT);
      }

      setHasMore((reset ? LIMIT : offset + LIMIT) < total);

    } catch (err) {
      console.error('Error loading matches:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
    }
  }, [status, uid, offset]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setOffset(0);
    await loadMatches(true);
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      loadMatches(false);
    }
  };

  useEffect(() => {
    if (status === 'ready' && uid) {
      loadMatches(true);
    }
  }, [status, uid]);

  const getResultColor = (result) => {
    if (result === 'Win') return 'text-green-600 bg-green-50';
    if (result === 'Lose') return 'text-red-600 bg-red-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getEloChangeColor = (delta) => {
    if (delta > 0) return 'text-green-600';
    if (delta < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  if (status === 'authenticating') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-100 via-blue-50 to-white flex items-center justify-center font-prompt">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังเข้าสู่ระบบ...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' && authError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-100 via-blue-50 to-white flex items-center justify-center p-4 font-prompt">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-800 text-sm">{authError}</p>
          </div>
          <Button onClick={retry} className="bg-green-600 hover:bg-green-700">
            ลองใหม่
          </Button>
        </div>
      </div>
    );
  }

  if (status !== 'ready') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-100 via-blue-50 to-white flex items-center justify-center font-prompt">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-100 via-blue-50 to-white pb-20 font-prompt">
      <div className="max-w-md mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">แมทซ์ของฉัน</h1>
          <Button 
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            size="sm"
          >
            <RotateCcw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Matches List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="p-4">
                <div className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-8 w-16" />
                </div>
              </Card>
            ))}
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium mb-2">ยังไม่มีแมทซ์</p>
            <p className="text-gray-400">เริ่มเล่นเพื่อดูประวัติการแข่งขัน</p>
          </div>
        ) : (
          <div className="space-y-3">
            {matches.map((match, index) => (
              <Card key={match.id || index} className="p-4">
                <div className="flex items-center space-x-4">
                  {/* Match Type Badge */}
                  <div className="flex flex-col items-center">
                    {match.is_rated ? (
                      <Medal className="h-5 w-5 text-blue-600 mb-1" />
                    ) : (
                      <Users className="h-5 w-5 text-gray-400 mb-1" />
                    )}
                    <Badge 
                      variant={match.is_rated ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {match.is_rated ? 'Ranked' : 'Unranked'}
                    </Badge>
                  </div>

                  {/* Opponent Info */}
                  <div className="flex items-center space-x-3 flex-1">
                    <Avatar className="h-10 w-10">
                      <AvatarImage 
                        src={match.opponent_avatar || match.opponent_picture} 
                        alt={match.opponent_name || 'Opponent'} 
                      />
                      <AvatarFallback className="bg-gray-200 text-gray-600">
                        {getInitials(match.opponent_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {formatDisplayName(match.opponent_name)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {match.opponent_new_rating ? `Elo: ${match.opponent_new_rating}` : 
                         match.opponent_rating ? `Elo: ${match.opponent_rating}` : 'Elo: —'}
                      </p>
                    </div>
                  </div>

                  {/* Match Result */}
                  <div className="text-right">
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getResultColor(match.result)}`}>
                      {match.result === 'Win' && <CheckCircle className="h-3 w-3 mr-1" />}
                      {match.result === 'Lose' && <XCircle className="h-3 w-3 mr-1" />}
                      {match.result || 'Unknown'}
                    </div>
                    {match.elo_delta !== undefined && match.elo_delta !== null && (
                      <p className={`text-sm font-medium mt-1 ${getEloChangeColor(match.elo_delta)}`}>
                        {match.elo_delta > 0 ? '+' : ''}{match.elo_delta}
                      </p>
                    )}
                  </div>
                </div>

                {/* Match Date */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    {formatDateTimeTH(match.played_at)}
                  </p>
                </div>
              </Card>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <div className="text-center pt-4">
                <Button 
                  onClick={loadMore}
                  disabled={loadingMore}
                  variant="outline"
                  className="w-full"
                >
                  {loadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      กำลังโหลด...
                    </>
                  ) : (
                    'โหลดเพิ่มเติม'
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">{error}</p>
            <Button 
              onClick={() => loadMatches(true)} 
              variant="outline" 
              size="sm" 
              className="mt-2"
            >
              ลองใหม่
            </Button>
          </div>
        )}
      </div>

      <BottomTabBar />
    </div>
  );
};

export default MyMatchesPage;